import { useSearchParams } from 'react-router-dom';

const PURCHASE_MODEL_STORAGE_KEY = 'purchase_model';
const PURCHASE_MODEL_COOKIE_NAME = 'pm';
const PURCHASE_MODEL_DIRECT = 'direct';
const PURCHASE_MODEL_TRIAL = 'trial';
const COOKIE_EXPIRY_DAYS = 90;

/**
 * Controls how the purchase model is selected for new signups.
 *
 * - 'force_direct' — every signup goes through the direct (immediate-pay) flow,
 *   regardless of URL params, localStorage, or cookies. (Current product
 *   direction.)
 * - 'force_trial'  — every signup goes through the trial flow, regardless of
 *   URL params, localStorage, or cookies.
 * - 'branching'    — original behavior: `?pm=direct` opts into direct (sticky
 *   via localStorage + cookie), otherwise fall back to trial.
 *
 * Both flows are intentionally preserved end-to-end (FE components,
 * member-backend `/v1/membership/trial/*` endpoints, Stripe `trial_period_days`
 * branch, catch_backend Temporal trial logic) so changing this constant
 * re-routes traffic without code changes elsewhere. Existing users already in
 * a Stripe `trialing` state are unaffected — this only controls the entry
 * path for new signups.
 */
type PurchaseModelMode = 'force_direct' | 'force_trial' | 'branching';
const PURCHASE_MODEL_MODE: PurchaseModelMode = 'force_direct';

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // Use .catchbio.com domain in production for cross-subdomain sharing (www ↔ app)
  const domainAttr = window.location.hostname.endsWith('.catchbio.com') ? ';domain=.catchbio.com' : '';
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax${domainAttr}`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolves the current purchase model from URL params, localStorage, or cookie.
 * Sticky: once a user is tagged as 'direct' via ?pm=direct, they stay in
 * the direct flow on return visits even without the param.
 */
export function getPurchaseModel(searchParams?: URLSearchParams): string {
  if (PURCHASE_MODEL_MODE === 'force_direct') {
    return PURCHASE_MODEL_DIRECT;
  }
  if (PURCHASE_MODEL_MODE === 'force_trial') {
    return PURCHASE_MODEL_TRIAL;
  }

  const urlParams = searchParams ?? new URLSearchParams(window.location.search);
  const pmParam = urlParams.get('pm');

  if (pmParam === PURCHASE_MODEL_DIRECT) {
    localStorage.setItem(PURCHASE_MODEL_STORAGE_KEY, PURCHASE_MODEL_DIRECT);
    setCookie(PURCHASE_MODEL_COOKIE_NAME, PURCHASE_MODEL_DIRECT, COOKIE_EXPIRY_DAYS);
    return PURCHASE_MODEL_DIRECT;
  }

  // Check localStorage (primary sticky store)
  const stored = localStorage.getItem(PURCHASE_MODEL_STORAGE_KEY);
  if (stored === PURCHASE_MODEL_DIRECT) {
    return PURCHASE_MODEL_DIRECT;
  }

  // Check cookie (fallback for cross-subdomain or cleared localStorage)
  const cookieValue = getCookie(PURCHASE_MODEL_COOKIE_NAME);
  if (cookieValue === PURCHASE_MODEL_DIRECT) {
    localStorage.setItem(PURCHASE_MODEL_STORAGE_KEY, PURCHASE_MODEL_DIRECT);
    return PURCHASE_MODEL_DIRECT;
  }

  return PURCHASE_MODEL_TRIAL;
}

/**
 * React hook that resolves the purchase model from URL, localStorage, or cookie.
 * Uses useSearchParams internally for router integration.
 */
export function usePurchaseModel(): { purchaseModel: string; isTrial: boolean } {
  const [searchParams] = useSearchParams();
  const model = getPurchaseModel(searchParams);
  return { purchaseModel: model, isTrial: model !== PURCHASE_MODEL_DIRECT };
}
